import Decimal from 'decimal.js-light';
import type { Infer } from 'superstruct';
import {
	array,
	boolean,
	coerce,
	coerce as coerceValue,
	defaulted,
	dynamic,
	enums,
	never,
	nullable,
	number,
	object,
	optional,
	partial,
	size,
	string,
} from 'superstruct';

import { ViewMediaDto } from '@adflow/shared/data-media';
import {
	parsableInteger,
	stringDate,
	stringMaxLength,
	userString,
	withMessage,
} from '@adflow/shared/util-superstruct';

import {
	AddGoogleAdsCampaignBudgetTemplateDto,
	ViewGoogleAdsCampaignBudgetTemplateDto,
} from './google-ads-campaign-budget.dto';
import {
	AddGoogleAdsCampaignLocationTemplateDto,
	ViewGoogleAdsCampaignLocationTemplateDto,
} from './google-ads-campaign-location.dto';
import { GoogleAdsFormConversionGoalDto } from './google-ads-conversion-goal.dto';
import {
	AddGoogleAdsGroupTemplateDto,
	ViewGoogleAdsGroupTemplateDto,
} from './google-ads-group.dto';
import {
	AddGoogleAdsSitelinkDto,
	ViewGoogleAdsSitelinkDto,
} from './google-ads-sitelink.dto';
import { ViewPromotionAssetDto } from './promotion-asset.dto';
import {
	AddStructuredSnippetDto,
	ViewStructuredSnippetDto,
} from './structured-snippet.dto';

const LocationTargetingDto = enums(['PRESENCE_OR_INTEREST', 'PRESENCE']);
const OptionalCalloutAssetDto = coerceValue(
	stringMaxLength(25),
	optional(nullable(string())),
	(value) => value ?? ''
);

export const ViewGoogleAdsCampaignTemplateDto = object({
	id: number(),
	name: string(),
	type: string(),
	status: string(),
	language: string(),
	network: optional(nullable(string())),
	label: optional(nullable(string())),
	startDate: stringDate(),
	endDate: stringDate(),

	budgetType: string(),
	budget: number(),
	biddingStrategyType: string(),

	useMerchantCenter: boolean(),
	includeLocations: boolean(),
	// targetingRadius: number(),

	groupTemplates: optional(array(ViewGoogleAdsGroupTemplateDto)),
	budgets: optional(array(ViewGoogleAdsCampaignBudgetTemplateDto)),
	locations: optional(array(ViewGoogleAdsCampaignLocationTemplateDto)),
	mobileAppCategories: optional(
		array(object({ id: number(), name: string() }))
	),
	campaignGoal: optional(string()),
	conversionGoals: optional(GoogleAdsFormConversionGoalDto),
	locationTargeting: LocationTargetingDto,
	promotionAssets: optional(array(ViewPromotionAssetDto)),
	images: optional(array(ViewMediaDto)),
	structuredSnippets: optional(array(ViewStructuredSnippetDto)),
	sitelinks: optional(array(ViewGoogleAdsSitelinkDto)),
	calloutAssets: optional(array(string())),
	rsaCampaignHeadlines: optional(array(string())),
	rsaCampaignDescriptions: optional(array(string())),
	enableAiMax: boolean(),
	aiMaxTextAssetAutomation: boolean(),
	aiMaxFinalUrlExpansionTextAssetAutomation: boolean(),
	aiMaxTermExclusions: optional(array(string())),
	aiMaxMessagingRestrictions: optional(array(string())),
});

export const AddGoogleAdsCampaignTemplateDto = object({
	id: optional(parsableInteger()),
	name: userString('validations:Required'),
	type: userString('validations:Required'),
	status: userString('validations:Required'),
	language: userString('validations:Required'),
	network: optional(nullable(string())),
	label: optional(nullable(string())),
	startDate: dynamic((value, context) => {
		if (value && typeof value === 'string') {
			const contextValues = context.branch.at(-2);
			const endDate = contextValues.endDate;
			const isGreater = new Date(value) >= new Date(endDate);
			if (isGreater) {
				return withMessage(
					never(),
					'validations:DateTime startDateTimeBeforeEnd'
				);
			}
		}
		return withMessage(stringDate(), 'validations:Required');
	}),
	endDate: withMessage(stringDate(), 'validations:Required'),

	budgetType: userString('validations:Required'),
	budget: withMessage(
		coerce(number(), string(), (value) =>
			value ? new Decimal(value).toNumber() : 0
		),
		'validations:Required'
	),
	biddingStrategyType: userString('validations:Required'),

	useMerchantCenter: boolean(),
	includeLocations: boolean(),
	// targetingRadius: number(),

	groupTemplates: optional(array(AddGoogleAdsGroupTemplateDto)),
	budgets: optional(array(AddGoogleAdsCampaignBudgetTemplateDto)),
	locations: optional(array(AddGoogleAdsCampaignLocationTemplateDto)),
	mobileAppCategories: optional(
		array(object({ id: number(), name: string() }))
	),
	campaignGoal: optional(string()),
	conversionGoals: optional(GoogleAdsFormConversionGoalDto),
	locationTargeting: LocationTargetingDto,
	promotionAssets: optional(array(object({ id: number() }))),
	images: defaulted(size(array(ViewMediaDto), 0, 20), []),
	structuredSnippets: optional(array(AddStructuredSnippetDto)),
	sitelinks: optional(array(AddGoogleAdsSitelinkDto)),
	calloutAssets: defaulted(size(array(OptionalCalloutAssetDto), 0, 10), []),
	rsaCampaignHeadlines: defaulted(size(array(stringMaxLength(30)), 0, 3), []),
	rsaCampaignDescriptions: defaulted(
		size(array(stringMaxLength(90)), 0, 2),
		[]
	),
	enableAiMax: defaulted(boolean(), false),
	aiMaxTextAssetAutomation: defaulted(boolean(), false),
	aiMaxFinalUrlExpansionTextAssetAutomation: defaulted(boolean(), false),
	aiMaxTermExclusions: defaulted(
		size(array(stringMaxLength(30, 'validations:Max Length Items')), 0, 25),
		[]
	),
	aiMaxMessagingRestrictions: defaulted(
		size(array(stringMaxLength(300, 'validations:Max Length Items')), 0, 40),
		[]
	),
});

export const EditGoogleAdsCampaignTemplateDto = partial(
	AddGoogleAdsCampaignTemplateDto
);

export type EditGoogleAdsCampaignTemplateDto = Infer<
	typeof EditGoogleAdsCampaignTemplateDto
>;

export type ViewGoogleAdsCampaignTemplateDto = Infer<
	typeof ViewGoogleAdsCampaignTemplateDto
>;

export type AddGoogleAdsCampaignTemplateDto = Infer<
	typeof AddGoogleAdsCampaignTemplateDto
>;
